import { Spacing } from '@persona-id/design-tokens';
import { FC } from 'react';
import styled from 'styled-components';

import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { Period } from 'dashboard/lib/unixTime';
import { DatumWithTimestamp } from 'dashboard/lib/utils/charting';
import {
  VerificationVolumeDatum,
  VerificationVolumeIntervals,
} from 'dashboard/models/metrics-verification';
import {
  DropdownItem,
  FilterDimensions,
} from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/VerificationFilteredMetrics';
import VerificationsComposedChart from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/VerificationsComposedChart';
import Card from 'design-system/components/Card';

interface VerificationComposedCardProps {
  barKeys: Partial<Record<'created_timestamp' | 'processed' | 'pass-rate' | 'group', string>>;
  filledProcessedDataForVerificationType: (Omit<VerificationVolumeDatum, 'created_timestamp'> &
    DatumWithTimestamp)[];
  groupByDimensionForType: FilterDimensions;
  groupItemsForType: DropdownItem[];
  groupedByInquiries: boolean;
  groupedVerificationType: boolean;
  lineKeys: Partial<Record<string, string>>;
  orderedProcessedLineKeys: Record<string, string>;
  periodicity: string;
  processedBarKeys: Record<string, string>;
  topValues: string[];
  topValuesVolume: string[];
  verificationVolumeOverTime: VerificationVolumeIntervals;
  chartPeriod: Period;
}

const VERIFICATION_CARD_BODY_HEIGHT = 384;

const legendIcons = {
  'pass rate': <HelpIcon> </HelpIcon>,
  processed: (
    <HelpIcon>
      {' '}
      This is the number of created Verifications that Persona was able to process and determine
      whether a Verification should pass, fail, or require a retry.
    </HelpIcon>
  ),
};

// Height is chart height + card padding
export const VerificationComposedCardBody = styled(Card.Body)`
  display: flex;
  flex-direction: column;
  padding-top: ${Spacing[1]};
  padding-left: ${Spacing[5]};
  height: ${VERIFICATION_CARD_BODY_HEIGHT}px;
`;

const VerificationMetricsChart: FC<VerificationComposedCardProps> = ({
  barKeys,
  filledProcessedDataForVerificationType,
  groupByDimensionForType,
  groupItemsForType,
  groupedByInquiries,
  groupedVerificationType,
  lineKeys,
  orderedProcessedLineKeys,
  periodicity,
  processedBarKeys,
  topValues,
  topValuesVolume,
  verificationVolumeOverTime,
  chartPeriod,
}) => {
  const initialFilteredKeys = groupedVerificationType
    ? topValues.concat(topValuesVolume)
    : ['processed', 'pass-rate'];

  const groupedByDimensionCopy = groupedVerificationType
    ? `Grouped by ${
        groupItemsForType.find((element: DropdownItem) => {
          return element.id === groupByDimensionForType;
        })?.name
      }`
    : '';
  const passRatesLabel = `Pass Rates ${groupedByDimensionCopy} (${periodicity})`;
  const chartLineKeys = groupedVerificationType ? orderedProcessedLineKeys : lineKeys;
  const chartBarKeys = groupedVerificationType ? processedBarKeys : barKeys;
  return (
    <VerificationComposedCardBody>
      <Card.Subtitle className='verification-summary__status-subtitle'>
        {passRatesLabel}
      </Card.Subtitle>
      <VerificationsComposedChart
        {...verificationVolumeOverTime}
        data={filledProcessedDataForVerificationType}
        lineKeys={chartLineKeys}
        linesLabel='Rate'
        barKeys={chartBarKeys}
        barsLabel='Volume'
        grouped={groupedVerificationType}
        initialFilteredKeys={initialFilteredKeys}
        groupedByInquiries={groupedByInquiries}
        chartPeriod={chartPeriod}
        legendIcons={legendIcons}
      />
    </VerificationComposedCardBody>
  );
};

export default VerificationMetricsChart;
